import { ComponentPropsWithRef, useCallback, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from '~shared/hooks';
import { isBrowser } from '~shared/utils/dom';
import {
  clampValue,
  getNumDecimalPlaces,
  toNumber,
  toPrecision,
} from '~shared/utils/number';

import { useSpinner } from './useSpinner';

type StringOrNumber = string | number;

export interface UseNumericInputProps
  extends Omit<ComponentPropsWithRef<'input'>, 'onChange'> {
  precision?: number;
  allowEmptyString?: boolean;

  onChange?(value: number | string): boolean | void;
}

function parse(value: StringOrNumber) {
  return toNumber(value.toString().replace(/[^\w.-]+/g, ''));
}

function format(
  value: StringOrNumber,
  step: StringOrNumber,
  precision?: number
) {
  const parsedValue = parse(value);
  const decimalPlaces = getNumDecimalPlaces(parsedValue, parse(step));
  const locale = 'en-US'; // @todo SOC-1592 Allow use of browser or app locale in future, currently this is causing issues

  return new Intl.NumberFormat([locale], {
    maximumFractionDigits: precision ?? decimalPlaces,
  }).format(parsedValue);
}

export function useNumericInput(props: UseNumericInputProps = {}) {
  const {
    defaultValue,
    value: valueProp,
    min,
    max,
    step: stepProp = 1,
    onChange,
    disabled: disabledProp,
    precision: precisionProp,
    allowEmptyString,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<StringOrNumber>(() => {
    if (defaultValue == null) return '';
    return format(defaultValue as StringOrNumber, stepProp, precisionProp);
  });

  useIsomorphicLayoutEffect(() => {
    if (valueProp) {
      setValue(valueProp as StringOrNumber);
    }
  }, [setValue, valueProp]);

  const decimalPlaces = getNumDecimalPlaces(parse(value), parse(stepProp));
  const precision = precisionProp ?? decimalPlaces;

  const update = useCallback(
    (next: StringOrNumber) => {
      if (next === '') {
        next = allowEmptyString ? '' : 0;
        onChange(next);
        setValue(next);
      } else {
        // NOTE: This next line is very brittle and a small change completely changes behavior of the input
        const continueUpdate = onChange?.(parse(next)) ?? true;
        if (continueUpdate) {
          setValue(next);
        }
      }
    },
    [onChange, allowEmptyString]
  );

  const clamp = useCallback(
    (value: number) => {
      const nextValue = clampValue(value, toNumber(min), toNumber(max));
      return toNumber(toPrecision(nextValue, precision));
    },
    [precision, max, min]
  );

  const increment = useCallback(
    (step = stepProp) => {
      let next: StringOrNumber;
      /**
       * Native browser behavior for empty value: if `min` is set,
       * start at `min`, otherwise start at `step`.
       */
      if (value === '') {
        next = parse(step);
      } else {
        const remainderToNextStep = (parse(value) + step) % step;
        next =
          remainderToNextStep === 0
            ? parse(value) + step
            : parse(value) + (step - remainderToNextStep);
      }

      next = clamp(next as number);
      update(next);
    },
    [stepProp, value, clamp, update]
  );

  const decrement = useCallback(
    (step = stepProp) => {
      let next: StringOrNumber;

      if (value === '') {
        next = parse(-step);
      } else {
        const remainderToNextStep = (parse(value) - step) % step;
        next =
          remainderToNextStep === 0
            ? parse(value) - step
            : parse(value) - remainderToNextStep;
      }

      next = clamp(next as number);
      update(next);
    },
    [stepProp, value, clamp, update]
  );

  const spinner = useSpinner(increment, decrement);

  const formatValue = useCallback(
    (next: StringOrNumber) => {
      const nextFormatted = format(next, stepProp, precision);
      if (nextFormatted !== value) {
        update(nextFormatted);
      }
    },
    [update, stepProp, precision, value]
  );

  const valueAsNumber = parse(value);
  const isOutOfRange =
    (max !== undefined && valueAsNumber > parse(max)) ||
    (min !== undefined && valueAsNumber < parse(min));
  const isAtMin = valueAsNumber === min;
  const isAtMax = valueAsNumber === max;

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const spinUp = useCallback(
    (event) => {
      event.preventDefault();
      spinner.up();
      focusInput();
    },
    [spinner, focusInput]
  );

  const spinDown = useCallback(
    (event) => {
      event.preventDefault();
      spinner.down();
      focusInput();
    },
    [spinner, focusInput]
  );

  const pointerDown =
    isBrowser && !!document.documentElement.ontouchstart
      ? 'onTouchStart'
      : 'onMouseDown';

  const getSpinUpButtonProps = useCallback(() => {
    const disabled = disabledProp || isAtMax;
    return {
      [pointerDown]: spinUp,
      onMouseUp: spinner.stop,
      onMouseLeave: spinner.stop,
      onTouchEnd: spinner.stop,
      disabled,
      'aria-disabled': disabled,
    };
  }, [disabledProp, isAtMax, pointerDown, spinUp, spinner.stop]);

  const getSpinDownButtonProps = useCallback(() => {
    const disabled = disabledProp || isAtMin;
    return {
      [pointerDown]: spinDown,
      onMouseUp: spinner.stop,
      onMouseLeave: spinner.stop,
      onTouchEnd: spinner.stop,
      disabled,
      'aria-disabled': disabled,
    };
  }, [disabledProp, isAtMin, pointerDown, spinDown, spinner.stop]);

  return {
    inputRef,
    value,
    valueAsNumber,
    isOutOfRange,
    isAtMin,
    isAtMax,
    update,
    increment,
    decrement,
    getSpinUpButtonProps,
    getSpinDownButtonProps,
    format: formatValue,
  };
}
